import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, Flex, Spinner, useColorModeValue } from '@chakra-ui/react';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import { Drawer, DrawerOverlay, DrawerContent, useDisclosure } from '@chakra-ui/react';

// Lazy-load every page so each becomes its own JS chunk
const Dashboard         = React.lazy(() => import('./pages/Dashboard'));
const Products          = React.lazy(() => import('./pages/Products'));
const Customers         = React.lazy(() => import('./pages/Customers'));
const Suppliers         = React.lazy(() => import('./pages/Suppliers'));
const Categories        = React.lazy(() => import('./pages/Categories'));
const Login             = React.lazy(() => import('./pages/Login'));
const SellInvoicesPage  = React.lazy(() => import('./pages/SellInvoicesPage'));
const ArrivalInvoicesPage = React.lazy(() => import('./pages/ArrivalInvoicesPage'));
const ReturnInvoicesPage  = React.lazy(() => import('./pages/ReturnInvoicesPage'));
const ProductCardPrinter  = React.lazy(() => import('./pages/ProductCardPrinter'));

const PageSpinner = () => (
  <Flex h="100vh" align="center" justify="center">
    <Spinner size="lg" color="green.500" thickness="3px" />
  </Flex>
);

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem('access');
  return token ? children : <Navigate to="/login" />;
};

const MainLayout = ({ isSidebarExpanded, setIsSidebarExpanded }) => {
  const location = useLocation();
  const showSidebar = location.pathname !== '/login';
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Reactive mobile detection — updates on resize/rotation
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const bgBase = useColorModeValue('#f5f7fc', '#0d1117');
  const bgGradient = useColorModeValue(
    'radial-gradient(ellipse at 15% 15%, rgba(102,126,234,0.13) 0%, transparent 55%), radial-gradient(ellipse at 85% 80%, rgba(118,75,162,0.11) 0%, transparent 55%), radial-gradient(ellipse at 70% 5%, rgba(79,172,254,0.09) 0%, transparent 45%)',
    'radial-gradient(ellipse at 15% 15%, rgba(102,126,234,0.18) 0%, transparent 55%), radial-gradient(ellipse at 85% 80%, rgba(118,75,162,0.15) 0%, transparent 55%), radial-gradient(ellipse at 70% 5%, rgba(79,172,254,0.10) 0%, transparent 45%)'
  );

  return (
    <>
      {/* Static CSS mesh-gradient background — zero JS, zero animation */}
      <Box
        position="fixed"
        top={0} left={0}
        w="100vw" h="100vh"
        zIndex={-1}
        pointerEvents="none"
        bg={bgBase}
        style={{ backgroundImage: bgGradient }}
      />
      <Header onMenuClick={isMobile && showSidebar ? onOpen : undefined} showMenuButton={isMobile && showSidebar} />
      {!isMobile && showSidebar && (
        <Sidebar isExpanded={isSidebarExpanded} setIsExpanded={setIsSidebarExpanded} />
      )}
      {isMobile && showSidebar && (
        <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
          <DrawerOverlay />
          <DrawerContent maxW="220px" p={0}>
            <Sidebar isExpanded={true} setIsExpanded={() => {}} />
          </DrawerContent>
        </Drawer>
      )}
      <Box
        ml={!isMobile && showSidebar ? (isSidebarExpanded ? "230px" : "110px") : "0"}
        pt="60px"
        px={{ base: 2, md: 4 }}
        pb={{ base: 2, md: 4 }}
      >
        <Suspense fallback={<PageSpinner />}>
          <Routes>
            <Route path="/"                   element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/categories"         element={<PrivateRoute><Categories /></PrivateRoute>} />
            <Route path="/products"           element={<PrivateRoute><Products /></PrivateRoute>} />
            <Route path="/customers"          element={<PrivateRoute><Customers /></PrivateRoute>} />
            <Route path="/suppliers"          element={<PrivateRoute><Suppliers /></PrivateRoute>} />
            <Route path="/sell-invoices"      element={<PrivateRoute><SellInvoicesPage /></PrivateRoute>} />
            <Route path="/arrival-invoices"   element={<PrivateRoute><ArrivalInvoicesPage /></PrivateRoute>} />
            <Route path="/return-invoices"    element={<PrivateRoute><ReturnInvoicesPage /></PrivateRoute>} />
            <Route path="/print-product-cards" element={<PrivateRoute><ProductCardPrinter /></PrivateRoute>} />
          </Routes>
        </Suspense>
      </Box>
    </>
  );
};

const App = () => {
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);

  return (
    <Router>
      <Suspense fallback={<PageSpinner />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/*"
            element={<MainLayout isSidebarExpanded={isSidebarExpanded} setIsSidebarExpanded={setIsSidebarExpanded} />}
          />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default App;
